import React, { useCallback } from 'react';
import {
  ButtonApprove,
  ButtonReject,
  FieldDatepicker,
  FieldInput,
  FieldTextArea,
  FormCol,
  FormRow,
  OptionType,
  useIsFieldRequiredHook,
} from 'motion-components';
import {
  FieldSelectCollector,
  FormFieldCollectionPointDlg,
  RowFieldTooltips,
} from 'shared-pages/components';
import { useFormikContext } from 'formik';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';

import { gql, useLazyQuery } from '@apollo/client';

import { FieldSelectContainerOrderType } from 'shared-pages/components/field-select/fs-queries';
import { ContainerOrderLinesTable } from './containerOrderLines-table';
import { ContainersOrderStates } from 'shared-pages/shared/constants';
import { BadgeStatusJudge } from '../BadgeStatusJudge';
import { Form } from 'react-bootstrap';
import { userInfo } from 'os';

const FormFields = ({ isNew, acceptRequest, rejectRequest, data, validationSchema }) => {
  const frmkCntx = useFormikContext();
  const { values, setFieldValue }: any = frmkCntx;
  const { authStore, appStore } = useRootStore();
  const { t } = useTranslation();
  const isFieldRequired = useIsFieldRequiredHook(validationSchema);

  const { handleGraphqlError } = useHandleError('OrderFormFields');
  const calcUserCanEdit = useCallback(() => {
    return {
      canEdit:
        isNew &&
        appStore.isPlatformOwner &&
        [null, undefined, ContainersOrderStates.Pending].includes(values?.statusId),
      canEditReasoning: '',
    };
  }, [isNew, appStore.isPlatformOwner, values?.statusId]);
  const calcUserCanInsert = useCallback(() => {
    const canInsert =
      isNew &&
      appStore.isPlatformOwner &&
      [null, undefined, ContainersOrderStates.Pending].includes(values?.statusId);
    return {
      canInsert,
      canInsertReasoning: '',
    };
  }, [isNew, appStore.isPlatformOwner, values?.statusId]);
  // 2. Invoke it (this will run only when the dependencies change)
  const { canEdit, canEditReasoning } = calcUserCanEdit();
  const { canInsert, canInsertReasoning } = calcUserCanInsert();

  const calcUserCanEditDelivery = useCallback(() => {
    return {
      canEditDelivery:
        values?.isApproved === true && (appStore.isPlatformOwner || appStore.isCollector),
      canEditDeliveryReasoning: '',
    };
  }, [isNew, appStore.isPlatformOwner, values?.statusId]);
  const { canEditDelivery, canEditDeliveryReasoning } = calcUserCanEditDelivery();

  function onCollectorIdChange(option: OptionType): void {
    if (option.value === null) return;
    if (values.collectorId === option.value) return;
    const collectorId = option.value;
    setFieldValue('collectorId', collectorId);
  }

  //queries
  const gqlGetCollectorForCollPoint = gql`
    query getCollectorForCollectionPoint($collectionPointId: Int) {
      getCollectorForCollectionPoint(collectionPointId: $collectionPointId)
    }
  `;
  const [getCollectionPoint] = useLazyQuery(gqlGetCollectorForCollPoint, {
    fetchPolicy: 'cache-first',
  });

  /*const [getCollPointContainers] = useLazyQuery(gqlGetCollPointContainers, {
    fetchPolicy: 'cache-first',
  });*/
  async function onCollectionPointIdChange(valueId: string, valueName: string): Promise<void> {
    // φερε τον Συλλέκτη του σημείου συλλογής απο τον σερβερ
    const ret = await getCollectionPoint({ variables: { collectionPointId: valueId } });
    const collectorId = ret?.data.getCollectorForCollectionPoint;
    if (collectorId) setFieldValue('collectorId', collectorId);
  }

  return (
    <div>
      {/* 1st Line */}
      <FormRow className="row justify-content-between mb-3 ">
        <FormCol className="col-md">
          <FieldInput label={t('Αριθμός Παραγγελίας')} name="orderNo" disabled={true} />
        </FormCol>
        <FormCol className="col-md">
          <FormFieldCollectionPointDlg
            label={t('Σημείο Συλλογής')}
            idFieldName="collectionPointId"
            nameFieldName="collectionpoint.name"
            onChange={onCollectionPointIdChange}
            disabled={!canEdit}
            required={values?.isCancelled ? false : isFieldRequired('collectionPointId', values)}
          />
        </FormCol>
        <FormCol className="col-md">
          <FieldDatepicker
            label={t('Ημερομηνία Παραγγελίας')}
            name="dateOrdered"
            disabled={!canEdit}
            required={values?.isCancelled ? false : isFieldRequired('dateOrdered', values)}
          />
        </FormCol>
        <FormCol className="col-md">
          {!isNew && (
            <div className="d-flex   align-items-start  flex-column mb-4">
              <span>{t('Κατάσταση Παραγγελίας')} :</span>
              <BadgeStatusJudge
                statusId={data?.statusId}
                isApproved={data?.isApproved}
                className="mt-2"
              />
            </div>
          )}
        </FormCol>
      </FormRow>

      {/* 2nd Line */}
      <FormRow className="row justify-content-between mb-3 ">
        <FormCol className="col-md">
          <FieldSelectCollector
            name="collectorId"
            label={t('Συλλέκτης')}
            onChange={onCollectorIdChange}
            disabled={!canEdit}
          />
        </FormCol>
        <FormCol className="col-md">
          <FieldSelectContainerOrderType
            name="containerorderTypeId"
            label={t('Τύπος Παραγγελίας')}
            disabled={!canEdit}
          />
        </FormCol>

        <FormCol className="col-md">
          <FieldTextArea label={t('Σημειώσεις')} name="notes" disabled={!canEdit} />
        </FormCol>

        <FormCol>
          {!isNew && (
            <FieldInput
              label={t('Αιτιολόγηση Αξιολόγησης')}
              name="judgeNotes"
              placeholder=""
              disabled={true}
            />
          )}
        </FormCol>
      </FormRow>

      {/* 3rd Line */}
      <FormRow>
        <FormCol />
        <FormCol />
        <FormCol>
          {!isNew && data?.isApproved && (
            <FieldDatepicker
              label={t('Ημερομηνία Παράδοσης')}
              name="datePosted"
              disabled={!canEditDelivery}
            />
          )}
        </FormCol>
        <FormCol>
          {!isNew && (
            <FieldInput
              label={t('Αξιολογήθηκε από')}
              name="judgedByUser"
              placeholder=""
              disabled={true}
            />
          )}
        </FormCol>
      </FormRow>

      {/* 4th Line */}
      <FormRow>
        <FormCol />
        <FormCol />
        <FormCol className="col-md">
          {!isNew && data?.isApproved && (
            <FieldTextArea
              label={t('Δελτίο Αποστολής')}
              name="sendDocumentId"
              disabled={!canEditDelivery}
            />
          )}
        </FormCol>
        <FormCol>
          {!isNew && (
            <FieldDatepicker
              label={t('Ημερομηνία Αξιολόγησης')}
              name="judgeDate"
              disabled={!canEdit}
            />
          )}
        </FormCol>
      </FormRow>
      {/* ******* Έγκριση / Απόρριψη BUTTONS ********* */}
      <FormRow>
        <FormCol />
        <FormCol />
        <FormCol />
        <FormCol>
          {data?.isApproved === null ? (
            <div className="w-100">
              <ButtonApprove
                onClick={() => acceptRequest(values)}
                size="sm"
                text={t('Αποδοχή')}
                disabled={data.requestLines?.length === 0}
                className="col-6"
                style={{ maxWidth: '49%' }}
              />

              <ButtonReject
                onClick={() => rejectRequest(values)}
                size="sm"
                text={t('Απόρριψη')}
                className="col-6 float-end"
                style={{ maxWidth: '49%' }}
              />
            </div>
          ) : null}
        </FormCol>
      </FormRow>
      <FormRow>
        <FormCol />
        <FormCol />
        <FormCol className="col-md">
          {!isNew && data?.isApproved && (
            <FieldTextArea label={t('Σημειώσεις Οδηγού')} name="drivernotes" />
          )}
        </FormCol>
      </FormRow>
      <RowFieldTooltips data={data} showLegacy={false} />

      {/* <EmptyFormRow /> */}

      {/* ******* Συσκευασίες ********* */}
      <div className="mb-5">
        {values && values?.lines && (
          <ContainerOrderLinesTable lines={values.lines} canEdit={canEdit} canInsert={canInsert} />
        )}
      </div>
    </div>
  );
};

export default FormFields;
